/**
 * Day-wise follow-ups for the Telecaller Workspace and the phone screen. PURE (callers
 * pass `now`). Asia/Kolkata day boundaries, the same as server/telecaller/followups.js.
 *
 * Groups: Overdue (every earlier IST day, oldest first) · Today · Tomorrow · then each
 * following date with its weekday. A row due earlier TODAY stays under Today (shown as
 * late), it is not "overdue" — overdue means a day has been missed.
 */

const IST_OFFSET = 5.5 * 3600 * 1000;
const DAY = 86400000;

export const istDayKey = (ms) => new Date(ms + IST_OFFSET).toISOString().slice(0, 10);
export const istDayStart = (ms) => { const s = ms + IST_OFFSET; return s - (s % DAY) - IST_OFFSET; };

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Sat 25 Oct" for an IST day key. */
export function dayKeyLabel(key) {
    const [y, m, d] = String(key).split('-').map(Number);
    if (!y || !m || !d) return String(key || '');
    const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
    return `${WEEKDAYS[wd]} ${d} ${MONTHS[m - 1]}`;
}

/** Which group a due time falls in: 'overdue' | 'today' | 'tomorrow' | 'YYYY-MM-DD'. */
export function groupKeyOf(dueAt, now) {
    const start = istDayStart(now);
    if (dueAt < start) return 'overdue';
    if (dueAt < start + DAY) return 'today';
    if (dueAt < start + 2 * DAY) return 'tomorrow';
    return istDayKey(dueAt);
}

/**
 * Group rows (each with a numeric `dueAt`) into ordered day groups. Rows without a due
 * time are left out. Within a group: earliest first.
 * @returns {{key: string, kind: 'overdue'|'today'|'tomorrow'|'day', label: string, count: number, overdue: number, rows: object[]}[]}
 */
export function groupFollowupsByDay(rows = [], now = Date.now()) {
    const todayKey = istDayKey(now);
    const tomorrowKey = istDayKey(istDayStart(now) + DAY);
    const map = new Map();
    for (const r of rows || []) {
        const due = Number(r?.dueAt);
        if (!Number.isFinite(due) || due <= 0) continue;
        const key = groupKeyOf(due, now);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(r);
    }
    const rank = (k) => (k === 'overdue' ? 0 : k === 'today' ? 1 : k === 'tomorrow' ? 2 : 3);
    return [...map.entries()]
        .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
        .map(([key, list]) => {
            list.sort((x, y) => Number(x.dueAt) - Number(y.dueAt));
            const kind = key === 'overdue' || key === 'today' || key === 'tomorrow' ? key : 'day';
            const label = key === 'overdue' ? 'Overdue'
                : key === 'today' ? `Today · ${dayKeyLabel(todayKey)}`
                : key === 'tomorrow' ? `Tomorrow · ${dayKeyLabel(tomorrowKey)}`
                : dayKeyLabel(key);
            return { key, kind, label, count: list.length, overdue: list.filter(x => Number(x.dueAt) <= now).length, rows: list };
        });
}

/** The compact strip: [{key, label, count}] — "Overdue 3 · Today 5 · Tomorrow 2 · Sat 25 Oct 1". */
export function dayStrip(groups = []) {
    return groups.map(g => ({
        key: g.key,
        label: g.kind === 'overdue' ? 'Overdue' : g.kind === 'today' ? 'Today' : g.kind === 'tomorrow' ? 'Tomorrow' : dayKeyLabel(g.key),
        count: g.count,
    }));
}

/** Range filter: 'today' | '7d' | '14d' | 'all'. Overdue is always included. */
export function withinRange(rows = [], range = 'today', now = Date.now()) {
    if (range === 'all') return rows;
    const days = range === '14d' ? 14 : range === '7d' ? 7 : 1;
    const horizon = istDayStart(now) + days * DAY;   // through the end of the last IST day
    return rows.filter(r => Number(r.dueAt) < horizon);
}

/**
 * One row shape for both sources: call follow-ups (tc/callbacks rows from
 * /api/tc/followups or /api/tc/followups/team) and pending To-Do tasks.
 */
export function mergeFollowupSources({ callbacks = [], tasks = [] } = {}) {
    const fromCalls = (callbacks || []).filter(Boolean).map(cb => ({
        key: `cb:${cb.id}`,
        id: cb.id,
        kind: 'callback',
        sourceLabel: 'Call follow-up',
        customer: cb.customerName || '',
        mobile: cb.mobile || cb.custKey || '',
        dueAt: Number(cb.dueAt) || 0,
        agentUid: cb.ownerUid || '',
        agent: cb.ownerName || '',
        reason: cb.reason || '',
        purpose: cb.purpose || '',
        status: cb.status || 'pending',
        raw: cb,
    }));
    const fromTasks = (tasks || []).filter(Boolean).map(t => ({
        key: `task:${t.id}`,
        id: t.id,
        kind: 'task',
        sourceLabel: 'Task',
        customer: t.customer || '',
        mobile: t.mobile || '',
        dueAt: Number(t.dueAt) || 0,
        agentUid: '',
        agent: t.assignedTo || '',
        reason: t.reason || '',
        purpose: t.source || '',
        status: 'pending',
        raw: t,
    }));
    return [...fromCalls, ...fromTasks].sort((a, b) => a.dueAt - b.dueAt);
}

/** "25 Oct, 4:30 pm" style label in IST, for one due time. */
export function fmtDueIst(ms) {
    if (!ms) return '—';
    return new Date(ms).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}
export function fmtTimeIst(ms) {
    if (!ms) return '—';
    return new Date(ms).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' });
}

/** `datetime-local` value (IST wall clock) for a ms time, and back. */
export function toIstLocalInput(ms) {
    return new Date(ms + IST_OFFSET).toISOString().slice(0, 16);
}
export function fromIstLocalInput(v) {
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(String(v || ''))) return 0;
    const t = Date.parse(`${String(v).slice(0, 16)}:00+05:30`);
    return Number.isFinite(t) ? t : 0;
}
